import { createModalStoreFactory } from '@/features/_shared/stores/create-modal.store';
import { useShallow } from 'zustand/shallow';
import type {
  IDeleteAppStoreChannelModalProps,
  TDeleteAppStoreChannelModalType,
} from '@/features/channels/app-store/components/modals/delete-app-store-channel-modal/delete-app-store-channel-modal';
import type {
  ISelectConnectAppStoreChannelModalProps,
  TSelectConnectAppStoreChannelModalType,
} from '@/features/channels/app-store/components/modals/select-connect-app-store-channel-modal/select-connect-app-store-channel-modal';
import type {
  ISendCredentialAppStoreChannelModalProps,
  TSendCredentialAppStoreChannelModalType,
} from '@/features/channels/app-store/components/modals/send-credential-app-store-modal/send-credential-app-store-modal';
import {
  IDeleteGooglePlayStoreChannelModalProps,
  TDeleteGooglePlayStoreChannelModalType,
} from '../../../google-play-store/components/modals/delete-google-play-store-channel-modal/delete-google-play-store-channel-modal';
import {
  IAddGooglePlayStoreApkModalProps,
  TAddGooglePlayStoreApkModalType,
} from '../../../google-play-store/components/modals/add-google-play-store-apk-modal/add-google-play-store-apk-modal';
import type {
  IAddLocationGoogleReviewModalProps,
  TAddLocationGoogleReviewModalType,
} from '../../../google-review/components/modals/add-location-google-review-modal/add-location-google-review-modal';
import type {
  IDeleteLocationGoogleReviewModalProps,
  TDeleteLocationGoogleReviewModalType,
} from '../../../google-review/components/modals/delete-location-google-review-modal/delete-location-google-review-modal';
import type {
  IDeleteIntegrationLineModalProps,
  TDeleteIntegrationLineModalType,
} from '../../../chat-platform/line/components/modals/delete-integration-line-modal/delete-integration-line-modal';
import type {
  IDeleteIntegrationWhatsappModalProps,
  TDeleteIntegrationWhatsappModalType,
} from '../../../chat-platform/whatsapp/components/modals/delete-integration-whatsapp-modal/delete-integration-whatsapp-modal';
import type {
  IGetQrWhatsappModalProps,
  TGetQrWhatsappModalType,
} from '../../../chat-platform/whatsapp/components/modals/get-qr-whatsapp-modal/get-qr-whatsapp-modal';
import type {
  ISwitchConnectWhatsappModalProps,
  TSwitchConnectWhatsappModalType,
} from '../../../chat-platform/whatsapp/components/modals/switch-connect-whatsapp-modal/switch-connect-whatsapp-modal';
import type {
  IAddPageMetaModalProps,
  TAddPageMetaModalType,
} from '../../../social-media/meta/components/modal/add-page-meta-modal/add-page-meta-modal';
import type {
  IDeleteIntegrationMetaModalProps,
  TDeleteIntegrationMetaModalType,
} from '../../../social-media/meta/components/modal/delete-integration-meta-modal/delete-integration-meta-modal';
import type {
  IDeleteIntegrationXModalProps,
  TDeleteIntegrationXModalType,
} from '../../../social-media/x/components/modals/delete-integration-x-modal/delete-integration-x-modal';
import type {
  IDeleteThreadsChannelModalProps,
  TDeleteThreadsChannelModalType,
} from '../../../social-media/threads/components/modals/delete-threads-channel-modal';
import type {
  IDeleteTiktokChannelModalProps,
  TDeleteTiktokChannelModalType,
} from '../../../social-media/tiktok/components/modals/delete-tiktok-channel-modal';
import {
  IDeleteIntegrationYoutubeModalProps,
  TDeleteIntegrationYoutubeModalType,
} from '@/features/channels/youtube/components/modals/delete-integration-youtube-modal/delete-integration-youtube-modal';

// --------- Channel Modal Root Types ---------
export type TChannelModalRootTypes =
  | TDeleteAppStoreChannelModalType
  | TSelectConnectAppStoreChannelModalType
  | TSendCredentialAppStoreChannelModalType
  | TDeleteGooglePlayStoreChannelModalType
  | TAddGooglePlayStoreApkModalType
  | TAddLocationGoogleReviewModalType
  | TDeleteLocationGoogleReviewModalType
  | TDeleteIntegrationLineModalType
  | TDeleteIntegrationWhatsappModalType
  | TGetQrWhatsappModalType
  | TSwitchConnectWhatsappModalType
  | TAddPageMetaModalType
  | TDeleteIntegrationMetaModalType
  | TDeleteIntegrationXModalType
  | TDeleteThreadsChannelModalType
  | TDeleteTiktokChannelModalType
  | TDeleteIntegrationYoutubeModalType;

// --------- Channel Modal Root Props ---------
type TChannelModalRootProps = {
  // App Store Channel Modal
  DeleteAppStoreChannelModal: IDeleteAppStoreChannelModalProps;
  SelectConnectAppStoreChannelModal: ISelectConnectAppStoreChannelModalProps;
  SendCredentialAppStoreChannelModal: ISendCredentialAppStoreChannelModalProps;

  DeleteGooglePlayStoreChannelModal: IDeleteGooglePlayStoreChannelModalProps;
  AddGooglePlayStoreApkModal: IAddGooglePlayStoreApkModalProps;

  AddLocationGoogleReviewModal: IAddLocationGoogleReviewModalProps;
  DeleteLocationGoogleReviewModal: IDeleteLocationGoogleReviewModalProps;

  DeleteIntegrationLineModal: IDeleteIntegrationLineModalProps;
  DeleteIntegrationWhatsappModal: IDeleteIntegrationWhatsappModalProps;
  GetQrWhatsappModal: IGetQrWhatsappModalProps;
  SwitchConnectWhatsappModal: ISwitchConnectWhatsappModalProps;

  // Social Media Meta Channel Modal
  AddPageMetaModal: IAddPageMetaModalProps;
  DeleteIntegrationMetaModal: IDeleteIntegrationMetaModalProps;

  // Social Media X Channel Modal
  DeleteIntegrationXModal: IDeleteIntegrationXModalProps;

  // Social Media Threads Channel Modal
  DeleteThreadsChannelModal: IDeleteThreadsChannelModalProps;

  // Social Media TikTok Channel Modal
  DeleteTiktokChannelModal: IDeleteTiktokChannelModalProps;

  // YouTube Channel Modal
  DeleteIntegrationYoutubeModal: IDeleteIntegrationYoutubeModalProps;
};

export const useChannelModalRootStore = createModalStoreFactory<TChannelModalRootTypes, TChannelModalRootProps>();

export const useChannelModalRootActionsStore = () => useChannelModalRootStore(useShallow((state) => state.actions));
